import jsPDF from "jspdf";

export const exportProductsToPDF = (products, options = {}) => {
  const {
    showEstimatedStock = false,
    showConfirmedStock = false,
    includeInactive = false,
    showManualColumn = true,
    showProductValue = false
  } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const marginLeft = 10;

  // Title and generation date on same line
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("Productenlijst", marginLeft, 15);

  // Add generation date on same line, right-aligned in small subtle font
  doc.setFontSize(6);
  doc.setFont(undefined, "normal");
  const dateStr = new Date().toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  doc.text(`Gegenereerd op: ${dateStr}`, pageWidth - marginLeft, 15, { align: 'right' });

  // Add legend below title
  doc.setFontSize(5);
  doc.setFont(undefined, "normal");
  doc.setTextColor(150, 150, 150); // Light gray color
  let legendY = 20;
  const showLegend = showConfirmedStock || showEstimatedStock || showManualColumn || showProductValue;
  if (showLegend) {
    const legendParts = [];
    if (showConfirmedStock) legendParts.push("BV = Bevestigde voorraad, voorraad van de recentste telling");
    if (showEstimatedStock) legendParts.push("GV = Geschatte voorraad sinds de laatste telling");
    if (showProductValue) {
      legendParts.push("P/E = Prijs per eenheid");
      legendParts.push("TW = Totale waarde");
    }
    if (showManualColumn) legendParts.push("T = Huidige telling - in te vullen");

    const legendText = "(" + legendParts.join(" / ") + ")";
    doc.text(legendText, marginLeft, legendY);
    legendY += 3;
  }
  doc.setTextColor(0, 0, 0); // Reset to black

  // Filter products based on options
  const filteredProducts = includeInactive
    ? products
    : products.filter(p => p.is_active !== false);

  // Group products by brand and calculate totals
  const productsByBrand = {};
  let totalValue = 0;

  filteredProducts.forEach((product) => {
    const brandName = product.brand || "Onbekend";
    if (!productsByBrand[brandName]) {
      productsByBrand[brandName] = [];
    }

    const price = product.purchase_price_incl_discount || 0;
    const stock = showEstimatedStock ? (product.estimated_stock || 0) : (product.confirmed_stock || 0);
    const value = price * stock;

    if (showProductValue) {
      totalValue += value;
    }

    productsByBrand[brandName].push({
      name: product.product_name,
      estimatedStock: product.estimated_stock || 0,
      confirmedStock: product.confirmed_stock || 0,
      price: price,
      value: value
    });
  });

  // Sort brands alphabetically
  const sortedBrands = Object.keys(productsByBrand).sort();

  // Add total value under legend if showProductValue is enabled
  let startY = 25;
  if (showLegend) {
    startY = 28;
    if (showProductValue) {
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      doc.setTextColor(0, 0, 0);
      const totalValueText = `Totale voorraadwaarde: €${totalValue.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      doc.text(totalValueText, marginLeft, startY);
      startY += 5;
    }
  }

  let yPositionLeft = startY;
  let yPositionRight = startY;
  const pageHeight = doc.internal.pageSize.height;
  const marginBottom = 10;
  const columnGap = 5;
  const columnWidth = (pageWidth - marginLeft * 2 - columnGap) / 2;

  // Calculate column widths based on options
  const numStockColumns = (showEstimatedStock ? 1 : 0) + (showConfirmedStock ? 1 : 0);
  const stockColumnWidth = numStockColumns > 0 ? 12 : 0; // Width per stock column
  const totalStockWidth = stockColumnWidth * numStockColumns;
  const manualEntryWidth = showManualColumn ? 12 : 0; // Width for manual entry column

  // Price and value columns when showProductValue is enabled
  const priceColumnWidth = showProductValue ? 14 : 0; // P/E column
  const valueColumnWidth = showProductValue ? 16 : 0; // TW column
  const totalValueWidth = priceColumnWidth + valueColumnWidth;

  const productNameWidth = columnWidth - totalStockWidth - manualEntryWidth - totalValueWidth;
  const rowHeight = 5;

  let currentColumn = 0; // 0 = left, 1 = right
  let isFirstTableInLeftColumn = true;
  let isFirstTableInRightColumn = true;

  const drawColumnHeader = (xOffset, startY) => {
    let localY = startY;
    let currentX = xOffset;

    doc.setFontSize(7);
    doc.setFont(undefined, "bold");
    doc.setTextColor(0, 0, 0); // Black text

    // Product name header
    doc.setFillColor(240, 240, 240); // Set gray fill before drawing
    doc.rect(currentX, localY - 3.5, productNameWidth, rowHeight, 'FD'); // 'FD' = Fill and Draw
    doc.text("Product", currentX + 1, localY);
    currentX += productNameWidth;

    // Confirmed stock header
    if (showConfirmedStock) {
      doc.setFillColor(240, 240, 240); // Set gray fill before drawing
      doc.rect(currentX, localY - 3.5, stockColumnWidth, rowHeight, 'FD');
      doc.text("BV", currentX + stockColumnWidth / 2, localY, { align: 'center' });
      currentX += stockColumnWidth;
    }

    // Estimated stock header
    if (showEstimatedStock) {
      doc.setFillColor(240, 240, 240); // Set gray fill before drawing
      doc.rect(currentX, localY - 3.5, stockColumnWidth, rowHeight, 'FD');
      doc.text("GV", currentX + stockColumnWidth / 2, localY, { align: 'center' });
      currentX += stockColumnWidth;
    }

    // Price per unit header (when showProductValue is enabled)
    if (showProductValue) {
      doc.setFillColor(240, 240, 240); // Set gray fill before drawing
      doc.rect(currentX, localY - 3.5, priceColumnWidth, rowHeight, 'FD');
      doc.text("P/E", currentX + priceColumnWidth / 2, localY, { align: 'center' });
      currentX += priceColumnWidth;
    }

    // Total value header (when showProductValue is enabled)
    if (showProductValue) {
      doc.setFillColor(240, 240, 240); // Set gray fill before drawing
      doc.rect(currentX, localY - 3.5, valueColumnWidth, rowHeight, 'FD');
      doc.text("TW", currentX + valueColumnWidth / 2, localY, { align: 'center' });
      currentX += valueColumnWidth;
    }

    // Manual entry header
    if (showManualColumn) {
      doc.setFillColor(240, 240, 240); // Set gray fill before drawing
      doc.rect(currentX, localY - 3.5, manualEntryWidth, rowHeight, 'FD');
      doc.text("T", currentX + manualEntryWidth / 2, localY, { align: 'center' });
    }

    // Reset fill color to white for subsequent drawings
    doc.setFillColor(255, 255, 255);

    localY += rowHeight + 2; // Add 2 units extra space after header
    return localY;
  };

  const drawBrandTable = (brand, xOffset, startY, drawHeader) => {
    let localY = startY;

    // Draw column header if this is the first table in the column
    if (drawHeader) {
      localY = drawColumnHeader(xOffset, localY);
    }

    // Brand header
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(brand, xOffset, localY);
    localY += 5;

    // Product rows
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    const brandProducts = productsByBrand[brand];
    brandProducts.forEach((product) => {
      let currentX = xOffset;

      // Product name column (with text wrapping support)
      const lines = doc.splitTextToSize(product.name, productNameWidth - 2);

      // Calculate row height based on text wrapping
      const actualRowHeight = Math.max(rowHeight, lines.length * 3.5);

      // Draw product name rectangle with correct height
      doc.rect(currentX, localY - 3.5, productNameWidth, actualRowHeight);
      doc.text(lines, currentX + 1, localY);
      currentX += productNameWidth;

      // Confirmed stock column
      if (showConfirmedStock) {
        doc.rect(currentX, localY - 3.5, stockColumnWidth, actualRowHeight);
        doc.text(String(product.confirmedStock), currentX + stockColumnWidth - 1, localY, { align: 'right' });
        currentX += stockColumnWidth;
      }

      // Estimated stock column
      if (showEstimatedStock) {
        doc.rect(currentX, localY - 3.5, stockColumnWidth, actualRowHeight);
        doc.text(String(product.estimatedStock), currentX + stockColumnWidth - 1, localY, { align: 'right' });
        currentX += stockColumnWidth;
      }

      // Price per unit column (when showProductValue is enabled)
      if (showProductValue) {
        doc.rect(currentX, localY - 3.5, priceColumnWidth, actualRowHeight);
        const priceText = product.price.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
        doc.text(priceText, currentX + priceColumnWidth - 1, localY, { align: 'right' });
        currentX += priceColumnWidth;
      }

      // Total value column (when showProductValue is enabled)
      if (showProductValue) {
        doc.rect(currentX, localY - 3.5, valueColumnWidth, actualRowHeight);
        const valueText = product.value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
        doc.text(valueText, currentX + valueColumnWidth - 1, localY, { align: 'right' });
        currentX += valueColumnWidth;
      }

      // Manual entry column
      if (showManualColumn) {
        doc.rect(currentX, localY - 3.5, manualEntryWidth, actualRowHeight);
      }

      localY += actualRowHeight;
    });

    // Add 1 empty row for manual entries
    let currentX = xOffset;
    doc.rect(currentX, localY - 3.5, productNameWidth, rowHeight);
    currentX += productNameWidth;

    if (showConfirmedStock) {
      doc.rect(currentX, localY - 3.5, stockColumnWidth, rowHeight);
      currentX += stockColumnWidth;
    }

    if (showEstimatedStock) {
      doc.rect(currentX, localY - 3.5, stockColumnWidth, rowHeight);
      currentX += stockColumnWidth;
    }

    if (showProductValue) {
      doc.rect(currentX, localY - 3.5, priceColumnWidth, rowHeight);
      currentX += priceColumnWidth;
    }

    if (showProductValue) {
      doc.rect(currentX, localY - 3.5, valueColumnWidth, rowHeight);
      currentX += valueColumnWidth;
    }

    if (showManualColumn) {
      doc.rect(currentX, localY - 3.5, manualEntryWidth, rowHeight);
    }
    localY += rowHeight;

    // Add one empty line after table
    localY += rowHeight;

    return localY;
  };

  sortedBrands.forEach((brand) => {
    const brandProducts = productsByBrand[brand];
    // Calculate height needed, add header height + extra space if it's the first table in column
    const headerHeight = (currentColumn === 0 && isFirstTableInLeftColumn) ||
                        (currentColumn === 1 && isFirstTableInRightColumn) ? rowHeight + 2 : 0;
    const heightNeeded = headerHeight + 5 + (brandProducts.length + 1) * rowHeight + rowHeight;

    // Determine which column to use and check if we need a new page
    if (currentColumn === 0) {
      // Left column
      if (yPositionLeft + heightNeeded > pageHeight - marginBottom) {
        // Check if right column has space
        if (yPositionRight + heightNeeded > pageHeight - marginBottom) {
          // Both columns full, new page
          doc.addPage();
          yPositionLeft = 10;
          yPositionRight = 10;
          isFirstTableInLeftColumn = true;
          isFirstTableInRightColumn = true;
        } else {
          // Switch to right column
          currentColumn = 1;
        }
      }
    } else {
      // Right column
      if (yPositionRight + heightNeeded > pageHeight - marginBottom) {
        // Right column full, new page
        doc.addPage();
        yPositionLeft = 10;
        yPositionRight = 10;
        currentColumn = 0;
        isFirstTableInLeftColumn = true;
        isFirstTableInRightColumn = true;
      }
    }

    const xOffset = currentColumn === 0
      ? marginLeft
      : marginLeft + columnWidth + columnGap;

    const startY = currentColumn === 0 ? yPositionLeft : yPositionRight;

    // Determine if we should draw header for this table
    const shouldDrawHeader = (currentColumn === 0 && isFirstTableInLeftColumn) ||
                            (currentColumn === 1 && isFirstTableInRightColumn);

    const finalY = drawBrandTable(brand, xOffset, startY, shouldDrawHeader);

    // Update Y position for the column we just used
    if (currentColumn === 0) {
      yPositionLeft = finalY;
      isFirstTableInLeftColumn = false; // Mark that we've drawn the first table
      currentColumn = 1; // Switch to right column for next brand
    } else {
      yPositionRight = finalY;
      isFirstTableInRightColumn = false; // Mark that we've drawn the first table
      currentColumn = 0; // Switch to left column for next brand
    }
  });

  // Save the PDF
  doc.save("productenlijst.pdf");
};
